import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import LoadingOverlay from 'react-loading-overlay';
import './GutAssessmentLanding.scss';
import gutAssessmentImages from '../gutAssessmentImages';
import { acceptGutAssessmentConsent, fetchGutAssessmentHistory, fetchGutAssessmentMetadata, fetchGutAssessmentPdpa } from '../gutAssessment.service';
import useReturnNavigation from '../../../hooks/useReturnNavigation';
import HnwHomeButton from '../../../common/hnwHomeButton/HnwHomeButton';

const ASSESSMENT_ID = process.env.REACT_APP_GUT_ASSESSMENT_ID || '';

const GutAssessmentLanding = () => {
  const history = useHistory();
  useReturnNavigation();
  const [isConsented, setIsConsented] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [latestAnswerId, setLatestAnswerId] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [pdpaContent, setPdpaContent] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [historyChecked, setHistoryChecked] = useState(false);

  useEffect(() => {
    const wrapper = document.getElementById('wrapper');
    if (wrapper) {
      wrapper.style.maxWidth = '100%';
    }
    return () => {
      if (wrapper) {
        wrapper.style.maxWidth = '';
      }
    };
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      try {
        setIsLoading(true);

        // Call metadata + pdpa first (interceptor will set customer in sessionStorage)
        const [data, pdpa] = await Promise.all([
          fetchGutAssessmentMetadata(ASSESSMENT_ID),
          fetchGutAssessmentPdpa(ASSESSMENT_ID),
        ]);
        setMetadata(data);
        setPdpaContent(pdpa);

        // Now customer is guaranteed in sessionStorage, call history
        const historyData = await fetchGutAssessmentHistory(ASSESSMENT_ID).catch(() => null);
        if (historyData && historyData.length > 0) {
          setHasHistory(true);
          const sorted = [...historyData].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
          setLatestAnswerId(sorted[0]._id);
        }
      } catch (err) {
        console.error('Failed to fetch assessment data:', err);
      } finally {
        setIsLoading(false);
        setHistoryChecked(true);
      }
    };
    loadAll();
  }, []);

  const handleConsentChange = () => {
    setIsConsented(!isConsented);
  };

  const handleLabelClick = (e) => {
    e.stopPropagation();
    setIsDrawerOpen(true);
  };

  const handleAcceptConsent = () => {
    setIsConsented(true);
    setIsDrawerOpen(false);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleStartAssessment = async () => {
    try {
      setIsLoading(true);
      await acceptGutAssessmentConsent();
      history.push(`/gut-assessment/question?assessment_id=${ASSESSMENT_ID}`);
    } catch (err) {
      console.error('Consent error:', err);
      history.push(`/gut-assessment/question?assessment_id=${ASSESSMENT_ID}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewHistory = () => {
    history.push(`/gut-assessment/result?assessment_id=${ASSESSMENT_ID}&answer_id=${latestAnswerId}`);
  };

  const backgroundImage = metadata?.landing_image_url
    ? `${process.env.REACT_APP_S3_URL}${metadata.landing_image_url}`
    : gutAssessmentImages.landing;

  const isReady = imageLoaded && historyChecked;

  return (
    <div className="gut-landing" data-testid="gut-landing-page">
      <HnwHomeButton className="hnw-home-button--absolute" />
      {!isReady && (
        <LoadingOverlay
          text="Loading"
          spinner
          active={true}
          styles={{ wrapper: { width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 10 } }}
        >
          <div />
        </LoadingOverlay>
      )}
      <div className="gut-landing__background" data-testid="gut-landing-background">
        <img
          className="gut-landing__background-image"
          src={backgroundImage}
          alt="Gut Health Assessment"
          data-testid="gut-landing-background-image"
          onLoad={() => setImageLoaded(true)}
        />
        {imageLoaded && historyChecked && (
        <div className="gut-landing__bottom-section">
          {!hasHistory && (
            <div className="gut-landing__consent" data-testid="gut-landing-consent" onClick={handleConsentChange}>
              <img
                className="gut-landing__consent-checkbox"
                data-testid="gut-landing-consent-checkbox"
                src={isConsented ? gutAssessmentImages.checkboxChecked : gutAssessmentImages.checkboxUnchecked}
                alt="checkbox"
              />
              <div className="gut-landing__consent-label" data-testid="gut-landing-consent-label" onClick={handleLabelClick}>
                ยอมรับเงื่อนไขการทำแบบประเมินสุขภาพลำไส้
              </div>
            </div>
          )}

          <div className="gut-landing__buttons" data-testid="gut-landing-buttons">
            <button
              className={`gut-landing__btn-start ${(hasHistory || isConsented) ? 'gut-landing__btn-start--active' : ''}`}
              data-testid="gut-landing-btn-start"
              disabled={!hasHistory && !isConsented}
              onClick={handleStartAssessment}
            >
              <span className="gut-landing__btn-start-text">เริ่มทำแบบประเมิน</span>
              <img className="gut-landing__btn-start-arrow" src={gutAssessmentImages.leftArrow} alt="arrow" />
            </button>
            <button
              className={`gut-landing__btn-history ${hasHistory ? 'gut-landing__btn-history--visible' : ''}`}
              data-testid="gut-landing-btn-history"
              onClick={handleViewHistory}
            >
              ดูประวัติการทำแบบประเมิน
            </button>
          </div>
        </div>
        )}
      </div>

      {isDrawerOpen && (
        <div className="gut-landing__drawer-overlay" data-testid="gut-landing-drawer-overlay" onClick={handleCloseDrawer}>
          <div className="gut-landing__drawer" data-testid="gut-landing-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="gut-landing__drawer-content" data-testid="gut-landing-drawer-content">
              {pdpaContent?.content ? (
                <div dangerouslySetInnerHTML={{ __html: pdpaContent.content }} />
              ) : (
                <>
                  <h3>เงื่อนไขการทำแบบประเมิน</h3>
                  <h4>ข้อกำหนดและเงื่อนไข</h4>
                  <p>1. การยอมรับข้อกำหนด เมื่อคุณใช้บริการนี้ถือว่าคุณยอมรับและตกลงปฏิบัติตามข้อกำหนดและเงื่อนไขเหล่านี้</p>
                  <p>2. การใช้งานบริการ คุณตกลงที่จะใช้บริการเพื่อวัตถุประสงค์ที่ถูกต้องตามกฎหมายและจะไม่กระทำการใด ๆ ที่อาจก่อให้เกิดความเสียหาย รบกวน หรือแทรกแซงการทำงานของบริการ</p>
                  <p>3. ความรับผิดชอบของบัญชีผู้ใช้ หากคุณสร้างบัญชี คุณมีหน้าที่รับผิดชอบในการเก็บรักษาข้อมูลเข้าสู่ระบบของคุณไว้เป็นความลับ และรับผิดชอบต่อการกระทำใด ๆ ที่เกิดขึ้นผ่านบัญชีของคุณ</p>
                  <p>4. ความเป็นส่วนตัว เราจะเก็บและประมวลผลข้อมูลส่วนบุคคลตามนโยบายความเป็นส่วนตัวของเรา โดยการใช้บริการนี้</p>
                </>
              )}
            </div>
            <button className="gut-landing__drawer-btn" data-testid="gut-landing-drawer-btn-accept" onClick={handleAcceptConsent}>
              ยอมรับเงื่อนไข
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GutAssessmentLanding;
